import { pick, curry, flatMap, fromPairs } from 'lodash';

import { GLOBAL_FIELDS } from '@datalas/common';

// Each datum includes the global fields and associated _label/_color fields
const BASE_FIELDS = flatMap(GLOBAL_FIELDS, (field) => [
  field,
  `${field}_label`,
  `${field}_color`,
]);

// Find a filter option object in `filters` based on key and value
const getOption = (filters, key, value) => {
  // Get the label/color from indicator/filtersByType
  const filter = filters[key] || {};
  const options = filter.options || [];
  return options.find((o) => o.name === value) || {};
};

// Derive value, label and color for a context from the filter set
const contextAttributes = (filters, context) => {
  const [key, value] = context.split(':');
  const option = getOption(filters, key, value);
  return {
    [key]: value,
    [`${key}_label`]: option.label || value,
    [`${key}_color`]: option.color,
  };
};

// All values must be numeric, unless they are null or NaN
const castValue = (value) => {
  if (value === null) return null;
  const numericVal = Number(value);
  return Number.isNaN(numericVal) ? value : numericVal;
};

// Get values specified in fields config from a datum
const getValues = (fields, datum) =>
  fromPairs(
    Object.entries(fields).map(([key, value]) => [
      key,
      castValue(datum[value]),
    ]),
  );

// Split the data so each unique combination becomes a single data point
const splitData = (data, filters, combinations = [], selectedFields = []) => {
  const filterAttributes = curry(contextAttributes)(filters);

  // Split each combination on each record into a separate record
  return flatMap(data, (record) => {
    // Each combination has the same base fields (raceth*, year, etc)
    const base = pick(record, BASE_FIELDS.concat(selectedFields));

    return combinations.map(({ contexts, fields }) => {
      // Add a new unique ID that accounts for the contexts
      const datum = { ...base };
      // Add filter values/labels/colors
      Object.entries(contexts)
        .map((kv) => kv.join(':'))
        .map(filterAttributes)
        .forEach((attributes) => {
          Object.assign(datum, attributes);
        });
      // Add value fields (data_column, mouseover_column_*, potentially more)
      Object.assign(datum, getValues(fields, record));

      return datum;
    });
  });
};

// Data that doesn't need to be split still needs UNIQUE_ID and coerced values
const normalizeData = (data, breakdown) => {
  if (!data.length) return [];

  const { valueField, mouseoverField } = breakdown;
  const fields = [valueField, mouseoverField]
    .filter((x) => x)
    .map((field) => ({ key: field, value: field }));

  return data.map((datum) => ({ ...datum, ...getValues(fields, datum) }));
};

/**
 * Split the data if it has combinations, otherwise just normalize it
 *
 * @param {object[]} data
 * @param {object} filters - Indicator filters by type
 * @param {object} breakdown - Breakdown config
 */
export default (data, filters, breakdown, selectedFields) => {
  const combinations = breakdown.combinations || [];
  return combinations.length
    ? splitData(data, filters, combinations, selectedFields)
    : normalizeData(data, breakdown);
};
